/**
 * Add playtest sheet (INVENTORY §2 row 5; prototype lines 1071-1145).
 * Two tabs: STEAM LINK / OFF-STEAM.
 *
 * Every validation string lives in the playtests slice / `lib/logic#ptCheckUrl`
 * and arrives as a toast or as `ptAddChk.err` — this file never re-implements
 * one (INVENTORY §8.6). The party-size gate is a real gate: `ptDoTrack` /
 * `ptDoAddOff` refuse with `PARTY SIZE REQUIRED` until one is picked.
 *
 * Verbatim §9 copy anchored here: the party-size gate, the Steam success
 * footer, the off-Steam header, and the off-Steam "OFFICIAL PAGES ONLY" footer.
 */

import { PtCover } from '../components/PtArt.tsx';
import { PixelButton } from '../components/PixelButton.tsx';
import { Sheet } from '../components/Sheet.tsx';
import { useQd } from '../store/index.ts';
import { ptAddVM } from '../store/selectors.3e.ts';
import type { PtPartyChip } from '../store/selectors.3e.ts';

const MONO = 'var(--gt-font-mono)';
const PIXEL = 'var(--gt-font-pixel)';

const FIELD = {
  background: 'var(--gt-field)',
  color: 'var(--gt-text)',
  fontFamily: MONO,
  fontSize: '10.5px',
  padding: '13px 12px',
  outline: 'none',
} as const;

function PartyRow({ party, onPick }: { party: PtPartyChip[]; onPick: (n: number) => void }) {
  return (
    <div style={{ display: 'flex', gap: 5, fontFamily: MONO, fontSize: '10.5px' }}>
      {party.map((p) => (
        <div
          key={p.key}
          onClick={() => onPick(p.n)}
          style={{
            flex: 1,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            border: '1px solid ' + p.bd,
            background: p.bg,
            color: p.clr,
            fontWeight: 600,
            height: 44,
            cursor: 'pointer',
            boxSizing: 'border-box',
          }}
        >
          {p.l}
        </div>
      ))}
    </div>
  );
}

function PartyGate() {
  return (
    <>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, flexWrap: 'wrap' }}>
        <span style={{ fontFamily: PIXEL, fontSize: 7, color: 'var(--gt-text)' }}>PARTY SIZE</span>
        <span style={{ fontFamily: MONO, fontSize: '8.5px', letterSpacing: '.06em', color: 'var(--acc2)' }}>REQUIRED</span>
      </div>
      <div style={{ fontFamily: MONO, fontSize: '8.5px', lineHeight: 1.6, color: 'var(--gt-muted-2)' }}>
        HOW MANY OF US CAN PLAY IN THE SAME GAME? STEAM DOESN&apos;T SAY
      </div>
    </>
  );
}

export default function PtAddSheet(): JSX.Element {
  const s = useQd();
  const v = ptAddVM(s);

  const tab = (clr: string, bd: string) => ({
    color: clr,
    borderBottom: '2px solid ' + bd,
    padding: '4px 2px 11px',
    cursor: 'pointer',
  });

  return (
    <Sheet
      label="Add playtest sheet"
      maxHeight="90vh"
      hs
      onClose={s.closePtAdd}
      header={{ title: '+ ADD A PLAYTEST', titleClr: 'var(--gt-text)', titleFs: '8.5px', note: '' }}
    >
      <div style={{ display: 'flex', gap: 18, padding: '13px 14px 0', fontFamily: MONO, fontSize: '10.5px', letterSpacing: '.05em' }}>
        <div onClick={() => s.setPtAddTab('steam')} style={tab(v.tabSClr, v.tabSBd)}>
          STEAM LINK
        </div>
        <div onClick={() => s.setPtAddTab('off')} style={tab(v.tabOClr, v.tabOBd)}>
          OFF-STEAM
        </div>
      </div>

      {v.isSteam ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 11, padding: '14px 14px 18px' }}>
          <input
            value={v.url}
            onChange={(e) => s.setPtAddUrl(e.target.value)}
            placeholder="https://store.steampowered.com/app/…"
            style={{ ...FIELD, border: '1px solid var(--gt-border-2)', color: 'var(--gt-text-2)' }}
          />
          <div
            onClick={() => void s.ptDoCheck()}
            style={{
              border: '1px solid var(--gt-text)',
              color: 'var(--gt-text)',
              fontFamily: MONO,
              fontSize: '10.5px',
              height: 44,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: v.checking ? 'progress' : 'pointer',
              boxSizing: 'border-box',
            }}
          >
            {v.checkLbl}
          </div>

          {v.hasErr ? (
            <div
              style={{
                border: '1px dashed var(--acc2)',
                padding: '12px 13px',
                fontFamily: MONO,
                fontSize: '9.5px',
                lineHeight: 1.7,
                color: 'var(--acc2)',
              }}
            >
              ✕ {v.err}
            </div>
          ) : null}

          {v.hasOk ? (
            <div
              style={{
                border: '1px solid ' + v.okBd,
                background: v.okBg,
                padding: '13px 12px',
                display: 'flex',
                flexDirection: 'column',
                gap: 11,
              }}
            >
              {/* P6 F-2: conditional on the real store page, not on "the URL parsed". */}
              <div style={{ fontFamily: PIXEL, fontSize: 7, color: v.okClr }}>{v.okTitle}</div>
              <div style={{ display: 'flex', gap: 11, alignItems: 'center' }}>
                <div style={{ position: 'relative', width: 96, aspectRatio: '460/215', flex: 'none', background: v.fall, overflow: 'hidden' }}>
                  <PtCover style={{ position: 'absolute', inset: 0 }} fs={9} id={'chk' + v.appid} ini={v.ini} cands={v.cands} />
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 4, minWidth: 0 }}>
                  <div style={{ fontWeight: 600, fontSize: '12.5px', color: 'var(--gt-text)' }}>{v.name}</div>
                  <div style={{ fontFamily: MONO, fontSize: 9, color: 'var(--gt-text-5)' }}>{v.dev} · UNRELEASED</div>
                  <div style={{ fontFamily: MONO, fontSize: 9, color: v.okClr }}>{v.sub}</div>
                </div>
              </div>

              <div style={{ borderTop: '1px dashed var(--gt-border)', paddingTop: 11, display: 'flex', flexDirection: 'column', gap: 9 }}>
                <PartyGate />
                <PartyRow party={v.party} onPick={s.setPtAddParty} />
              </div>

              <PixelButton
                label={v.trackLbl}
                busy={v.tracking}
                onClick={() => void s.ptDoTrack()}
                fs={8}
                pad="14px 10px"
                center
                lh="1"
                shadow={false}
              />

              <div style={{ fontFamily: MONO, fontSize: 8, lineHeight: 1.6, color: 'var(--gt-muted-2)' }}>
                STATUS RE-CHECKED NIGHTLY · OPENS/CLOSES + DETECTED WAVES POST TO WHAT&apos;S NEW · ATTRIBUTED TO YOU
              </div>
            </div>
          ) : null}
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 11, padding: '14px 14px 18px' }}>
          <div style={{ fontFamily: MONO, fontSize: '8.5px', letterSpacing: '.06em', lineHeight: 1.7, color: 'var(--gt-muted-2)' }}>
            FOR BUNGIE.NET / DEV-SITE / DISCORD-ANNOUNCED TESTS — USER-MAINTAINED, EVERY EDIT ATTRIBUTED
          </div>
          <input
            value={v.offName}
            onChange={(e) => s.setPtOff({ ptOffName: e.target.value })}
            placeholder="GAME NAME"
            style={{ ...FIELD, border: '1px solid var(--gt-border)' }}
          />
          <input
            value={v.offUrl}
            onChange={(e) => s.setPtOff({ ptOffUrl: e.target.value })}
            placeholder="OFFICIAL SIGNUP URL"
            style={{ ...FIELD, border: '1px solid var(--gt-border)' }}
          />
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
            <input
              type="date"
              value={v.offStart}
              onChange={(e) => s.setPtOff({ ptOffStart: e.target.value })}
              style={{ ...FIELD, border: '1px solid var(--gt-border)', color: 'var(--gt-text-5)', fontSize: 10, padding: '12px 11px' }}
            />
            <input
              type="date"
              value={v.offEnd}
              onChange={(e) => s.setPtOff({ ptOffEnd: e.target.value })}
              style={{ ...FIELD, border: '1px solid var(--gt-border)', color: 'var(--gt-text-5)', fontSize: 10, padding: '12px 11px' }}
            />
          </div>

          <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, flexWrap: 'wrap' }}>
            <span style={{ fontFamily: PIXEL, fontSize: 7, color: 'var(--gt-text)' }}>PARTY SIZE</span>
            <span style={{ fontFamily: MONO, fontSize: '8.5px', letterSpacing: '.06em', color: 'var(--acc2)' }}>REQUIRED</span>
          </div>
          <PartyRow party={v.party} onPick={s.setPtAddParty} />

          <PixelButton
            label={v.trackLbl}
            busy={v.tracking}
            onClick={() => void s.ptDoAddOff()}
            fs={8}
            pad="14px 10px"
            center
            lh="1"
            shadow={false}
          />

          <div style={{ fontFamily: MONO, fontSize: 8, lineHeight: 1.7, color: 'var(--gt-muted-2)' }}>
            OFFICIAL PAGES ONLY — LINKS THAT LOOK LIKE CHAT INVITES GET FLAGGED. NO DATES? IT LISTS UNDER “NO DATED WINDOW”.
          </div>
        </div>
      )}
    </Sheet>
  );
}
